import type { ReactNode } from 'react';
import type { WebsiteIcon as WebsiteIconEnum } from 'autogen/sharedEnums';
import type { ShippingSupplier } from 'autogen/swagger/all';
import dynamic from 'helpers/dynamic';
import useSegmentation from 'hooks/user/useSegmentation';

// Payment
const Dankort = dynamic(() => import('icons/payments-providers/Dankort.svg?react'));
const Klarna = dynamic(() => import('icons/payments-providers/Klarna.svg?react'));
const Mastercard = dynamic(() => import('icons/payments-providers/Mastercard.svg?react'));
const MobilepayVertical = dynamic(() => import('icons/payments-providers/MobilepayVertical.svg?react'));
const MobilepayHorizontal = dynamic(() => import('icons/payments-providers/MobilepayHorizontal.svg?react'));
const Swish = dynamic(() => import('icons/payments-providers/Swish.svg?react'));
const Vipps = dynamic(() => import('icons/payments-providers/Vipps.svg?react'));
const Visa = dynamic(() => import('icons/payments-providers/Visa.svg?react'));
const GooglePay = dynamic(() => import('icons/payments-providers/Googlepay.svg?react'));
const ApplePay = dynamic(() => import('icons/payments-providers/Applepay.svg?react'));
const Anyday = dynamic(() => import('icons/payments-providers/Anyday.svg?react'));

// Shipping
const Bring = dynamic(() => import('icons/delivery-providers/Bring.svg?react'));
const Posten = dynamic(() => import('icons/delivery-providers/Posten.svg?react'));
const Burd = dynamic(() => import('icons/delivery-providers/Burd.svg?react'));
const Dao = dynamic(() => import('icons/delivery-providers/Dao.svg?react'));
const GLS = dynamic(() => import('icons/delivery-providers/GLS.svg?react'));
const Instabox = dynamic(() => import('icons/delivery-providers/Instabox.svg?react'));
const Postnord = dynamic(() => import('icons/delivery-providers/Postnord.svg?react'));
const Helthjem = dynamic(() => import('icons/delivery-providers/Helthjem.svg?react'));
const Matas = dynamic(() => import('icons/delivery-providers/Matas.svg?react'));
const Kicks = dynamic(() => import('icons/delivery-providers/Kicks.svg?react'));
const Budbee = dynamic(() => import('icons/delivery-providers/Budbee.svg?react'));
const CityMail = dynamic(() => import('icons/delivery-providers/CityMail.svg?react'));
const Posti = dynamic(() => import('icons/delivery-providers/Posti.svg?react'));
const Airmee = dynamic(() => import('icons/delivery-providers/Airmee.svg?react'));

// App stores
const AppStore = dynamic(() => import('icons/app-stores/AppStore.svg?react'));
const PlayStore = dynamic(() => import('icons/app-stores/PlayStore.svg?react'));

type WebsiteIconProps = {
    icon: WebsiteIconEnum;
    useVertical?: boolean;
} & React.ComponentPropsWithoutRef<'svg'>;

function WebsiteIcon({ icon, useVertical, ...restProps }: WebsiteIconProps): ReactNode {
    const { tenant, countryCode } = useSegmentation();

    switch (icon) {
        case 'AppStore_Apple':
            return <AppStore {...restProps} />;
        case 'AppStore_GooglePlay':
            return <PlayStore {...restProps} />;
        case 'Payment_Anyday':
            return <Anyday {...restProps} />;
        case 'Payment_ApplePay':
            return <ApplePay {...restProps} />;
        case 'Payment_Dankort':
            return <Dankort {...restProps} />;
        case 'Payment_GooglePay':
            return <GooglePay {...restProps} />;
        case 'Payment_Klarna':
            return <Klarna {...restProps} />;
        case 'Payment_MasterCard':
            return <Mastercard {...restProps} />;
        case 'Payment_MobilePay':
            if (useVertical) return <MobilepayVertical {...restProps} />;
            return <MobilepayHorizontal {...restProps} />;
        case 'Payment_Swish':
            return <Swish {...restProps} />;
        case 'Payment_Vipps':
            return <Vipps {...restProps} />;
        case 'Payment_Visa':
            return <Visa {...restProps} />;
        case 'Shipping_Bring':
            return countryCode === 'NO' ? <Posten {...restProps} /> : <Bring {...restProps} />;
        case 'Shipping_Burd':
            return <Burd {...restProps} />;
        case 'Shipping_DAO':
            return <Dao {...restProps} />;
        case 'Shipping_GLS':
            return <GLS {...restProps} />;
        case 'Shipping_HeltHjem':
            return <Helthjem {...restProps} />;
        case 'Shipping_Instabox':
            return <Instabox {...restProps} />;
        case 'Shipping_PostNord':
            return <Postnord {...restProps} />;
        case 'Shipping_Matas':
            return tenant === 'Matas' ? <Matas {...restProps} /> : <Kicks {...restProps} />;
        case 'Shipping_Budbee':
            return <Budbee {...restProps} />;
        case 'Shipping_CityMail':
            return <CityMail {...restProps} />;
        case 'Shipping_Posti':
            return <Posti {...restProps} />;
        case 'Shipping_Airmee':
            return <Airmee {...restProps} />;
        default:
            console.error(`Parameter type '${icon}' is not supported`);
            return null;
    }
}

export type ShippingSupplierIconProps = {
    icon: ShippingSupplier;
};

/**
 * This function is used to render the shipping supplier icon based on the ShippingSupplier type.
 */
function ShippingSupplierIcon(props: ShippingSupplierIconProps) {
    const { icon, ...restProps } = props;

    switch (icon) {
        case 'Digital':
        case 'DropShip':
            return null;
        case 'Burd':
        case 'BurdEconomy':
            return <WebsiteIcon icon="Shipping_Burd" {...restProps} />;
        case 'HeltHjem':
        case 'HeltHjemExpress':
            return <WebsiteIcon icon="Shipping_HeltHjem" {...restProps} />;
        default:
            return <WebsiteIcon icon={`Shipping_${icon}`} {...restProps} />;
    }
}

export default Object.assign(WebsiteIcon, {
    ShippingSupplierIcon
});
